import React, { useState, useEffect } from 'react';
import { X, Send, CheckCircle } from 'lucide-react';
import { pollsAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface Question {
  id: number;
  question_text: string;
  question_type: 'text' | 'textarea' | 'radio' | 'checkbox' | 'dropdown' | 'rating' | 'date';
  is_required: boolean | number; // Can be boolean or number (0/1) from database
  options: string[] | null;
  display_order?: number;
}

interface Poll {
  id: number;
  title: string;
  description: string | null;
  is_anonymous: boolean | number; // Can be boolean or number (0/1) from database
  deadline: string | null;
  allow_responses_after_deadline?: boolean | number; // Can be boolean or number (0/1) from database
  questions: Question[];
  user_has_responded?: boolean;
  response_count?: number; // May be present but should not be displayed in response form
}

interface PollResponseFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  poll: Poll | null;
}

const PollResponseForm: React.FC<PollResponseFormProps> = ({ isOpen, onClose, onSuccess, poll }) => {
  const [responses, setResponses] = useState<Record<number, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (poll && isOpen) {
      // Initialize responses object
      const initialResponses: Record<number, any> = {};
      poll.questions.forEach(q => {
        if (q.question_type === 'checkbox') {
          initialResponses[q.id] = [];
        } else if (q.question_type === 'rating') {
          initialResponses[q.id] = null;
        } else {
          initialResponses[q.id] = '';
        }
      });
      setResponses(initialResponses);
      setSubmitted(false);
    }
  }, [poll, isOpen]);

  const updateResponse = (questionId: number, value: any) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleCheckboxChange = (questionId: number, option: string, checked: boolean) => {
    setResponses(prev => {
      const current = prev[questionId] || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, option] };
      } else {
        return { ...prev, [questionId]: current.filter((v: string) => v !== option) };
      }
    });
  };

  const validateResponses = (): boolean => {
    if (!poll) {
      return false;
    }
    
    if (!poll.questions || poll.questions.length === 0) {
      return true; // No questions to validate
    }
    
    for (const question of poll.questions) {
      // Check if question is required (handle both boolean and number 0/1 from database)
      const isRequired = question.is_required === true || question.is_required === 1;
      
      if (isRequired) {
        const response = responses[question.id];
        
        if (question.question_type === 'checkbox') {
          if (!response || !Array.isArray(response) || response.length === 0) {
            toast.error(`Please answer the required question: ${question.question_text}`);
            return false;
          }
        } else if (question.question_type === 'rating') {
          if (response === null || response === undefined || response === '') {
            toast.error(`Please answer the required question: ${question.question_text}`);
            return false;
          }
        } else {
          if (!response || (typeof response === 'string' && response.trim() === '')) {
            toast.error(`Please answer the required question: ${question.question_text}`);
            return false;
          }
        }
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!poll) {
      toast.error('Poll data is missing');
      return;
    }

    const isValid = validateResponses();
    
    if (!isValid) {
      return;
    }
    
    // Check deadline
    if (poll.deadline) {
      // Parse MySQL datetime as local time
      const dateStr = poll.deadline;
      let deadlineDate: Date;
      if (typeof dateStr === 'string' && dateStr.includes(' ') && !dateStr.includes('T') && !dateStr.includes('Z')) {
        // Parse manually to ensure local time (not UTC)
        const [datePart, timePart] = dateStr.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes, seconds] = timePart.split(':').map(Number);
        deadlineDate = new Date(year, month - 1, day, hours, minutes, seconds || 0);
      } else {
        deadlineDate = new Date(dateStr);
      }
      const now = new Date();
      const allowAfterDeadline = poll.allow_responses_after_deadline === true || poll.allow_responses_after_deadline === 1;
      
      if (deadlineDate < now && !allowAfterDeadline) {
        toast.error('This poll has passed its deadline and is no longer accepting responses.');
        return;
      } else if (deadlineDate < now && allowAfterDeadline) {
        // Show a warning but allow submission
        toast('This poll has passed its deadline, but responses are still being accepted.', {
          icon: '⚠️',
          duration: 4000
        });
      }
    }

    setIsSubmitting(true);
    
    try {
      await pollsAPI.submitResponse(poll.id, responses);
      toast.success('Response submitted successfully!');
      setSubmitted(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to submit response');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !poll) return null;

  if (submitted) {
    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Response Submitted!</h2>
          <p className="text-gray-600">Thank you for your response.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{poll.title}</h2>
            {poll.description && (
              <p className="text-sm text-gray-600 mt-1">{poll.description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {poll.deadline && (() => {
            // Parse MySQL datetime as local time
            const dateStr = poll.deadline;
            let deadlineDate: Date;
            if (typeof dateStr === 'string' && dateStr.includes(' ') && !dateStr.includes('T') && !dateStr.includes('Z')) {
              // Parse manually to ensure local time (not UTC)
              // Format: "YYYY-MM-DD HH:MM:SS"
              const [datePart, timePart] = dateStr.split(' ');
              const [year, month, day] = datePart.split('-').map(Number);
              const [hours, minutes, seconds] = timePart.split(':').map(Number);
              deadlineDate = new Date(year, month - 1, day, hours, minutes, seconds || 0);
            } else {
              deadlineDate = new Date(dateStr);
            }
            const now = new Date();
            const isPastDeadline = deadlineDate < now;
            const allowAfterDeadline = poll.allow_responses_after_deadline === true || poll.allow_responses_after_deadline === 1;
            
            // Format deadline for display (as local time)
            const formattedDeadline = deadlineDate.toLocaleString('en-US', {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            });
            
            return (
              <div className={`border rounded-lg p-3 ${
                isPastDeadline && !allowAfterDeadline 
                  ? 'bg-red-50 border-red-200' 
                  : isPastDeadline && allowAfterDeadline
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <p className={`text-sm ${
                  isPastDeadline && !allowAfterDeadline 
                    ? 'text-red-800' 
                    : isPastDeadline && allowAfterDeadline
                    ? 'text-yellow-800'
                    : 'text-blue-800'
                }`}>
                  <strong>Deadline:</strong> {formattedDeadline}
                  {isPastDeadline && !allowAfterDeadline && (
                    <span className="block mt-1 font-medium">⚠️ This poll has passed its deadline and is no longer accepting responses.</span>
                  )}
                  {isPastDeadline && allowAfterDeadline && (
                    <span className="block mt-1">⚠️ This poll has passed its deadline, but responses are still being accepted.</span>
                  )}
                </p>
              </div>
            );
          })()}

          {(poll.is_anonymous === true || poll.is_anonymous === 1) && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-700">
                This poll is anonymous. Your responses will not be linked to your identity.
              </p>
            </div>
          )}

          {/* Questions Section */}
          {(() => {
            const validQuestions = Array.isArray(poll.questions) 
              ? poll.questions
                  .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999))
                  .filter(question => question && question.id && question.question_text)
              : [];
            
            if (validQuestions.length === 0) {
              return null;
            }
            
            return validQuestions.map((question, index) => {
              // Always use index + 1 for question numbering (1-based)
              const questionNumber = index + 1;
              // Convert is_required to boolean for HTML required attribute
              const isRequired = question.is_required === true || question.is_required === 1;
              
              return (
                <div key={question.id} className="space-y-2">
                  <div className="block text-sm font-medium text-gray-900">
                    {questionNumber}. {question.question_text}
                    {question.is_required === true ? <span className="text-red-500 ml-1">*</span> : null}
                  </div>

              {question.question_type === 'text' && (
                <input
                  type="text"
                  value={responses[question.id] || ''}
                  onChange={(e) => updateResponse(question.id, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  required={isRequired}
                />
              )}

              {question.question_type === 'textarea' && (
                <textarea
                  value={responses[question.id] || ''}
                  onChange={(e) => updateResponse(question.id, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  rows={4}
                  required={isRequired}
                />
              )}

              {question.question_type === 'radio' && question.options && (
                <div className="space-y-2">
                  {question.options.map((option, optIndex) => (
                    <label key={optIndex} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name={`question-${question.id}`}
                        value={option}
                        checked={responses[question.id] === option}
                        onChange={(e) => updateResponse(question.id, e.target.value)}
                        className="w-4 h-4 text-[var(--accent)] border-gray-300 focus:ring-[var(--accent)]"
                        required={isRequired}
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {question.question_type === 'checkbox' && question.options && (
                <div className="space-y-2">
                  {question.options.map((option, optIndex) => (
                    <label key={optIndex} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(responses[question.id] || []).includes(option)}
                        onChange={(e) => handleCheckboxChange(question.id, option, e.target.checked)}
                        className="w-4 h-4 text-[var(--accent)] border-gray-300 rounded focus:ring-[var(--accent)]"
                      />
                      <span className="text-sm text-gray-700">{option}</span>
                    </label>
                  ))}
                </div>
              )}

              {question.question_type === 'dropdown' && question.options && (
                <select
                  value={responses[question.id] || ''}
                  onChange={(e) => updateResponse(question.id, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  required={isRequired}
                >
                  <option value="">Select an option...</option>
                  {question.options.map((option, optIndex) => (
                    <option key={optIndex} value={option}>{option}</option>
                  ))}
                </select>
              )}

              {question.question_type === 'rating' && (
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      type="button"
                      onClick={() => updateResponse(question.id, rating)}
                      className={`w-10 h-10 rounded-lg border-2 transition-colors ${
                        responses[question.id] === rating
                          ? 'bg-[var(--accent)] text-white border-[var(--accent)]'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-[var(--accent)]'
                      }`}
                    >
                      {rating}
                    </button>
                  ))}
                  <span className="text-sm text-gray-600 ml-2">
                    {responses[question.id] ? `Selected: ${responses[question.id]}` : 'Select rating'}
                  </span>
                </div>
              )}

              {question.question_type === 'date' && (
                <input
                  type="date"
                  value={responses[question.id] || ''}
                  onChange={(e) => updateResponse(question.id, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  required={isRequired}
                />
              )}
                </div>
              );
            });
          })()}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg bg-[var(--accent)] text-white font-medium hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
            >
              <Send className="w-4 h-4" />
              {isSubmitting ? 'Submitting...' : 'Submit Response'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PollResponseForm;
